import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { FindRequestDto } from '../models/find-request.dto';
import { REQUEST_STATUS_OPTIONS } from '../models/request-status.constants';
import { DischargeInfo, RequestDto } from '../models/request.dto';
import { AuthService } from './auth.service';
import { Actor } from './context.service';

@Injectable({ providedIn: 'root' })
export class RequestService {
  private baseUrl = environment.beServiceUrl;
  readonly requestStatusOptions = REQUEST_STATUS_OPTIONS;

  constructor(private http: HttpClient, private authService: AuthService) {}

  getRequests(
    request: FindRequestDto
  ): Observable<{ total: number; transitions: RequestDto[] }> {
    return this.http.post<{ total: number; transitions: RequestDto[] }>(
      this.baseUrl + '/v1/transition/find',
      request
    );
  }

  getCauses(type?: string) {
    const url= type ? this.baseUrl + `/v1/cause?noteTypeCode=${type}` : this.baseUrl + `/v1/cause`;
    return this.http.get<any>(url);
  }

    loadActors(isReportingEntity?: boolean) {
      let url = `${environment.beServiceUrl}/v1/actor/find`;
      if(isReportingEntity!= undefined) {
        url = url+ `?isReportingEntity=${isReportingEntity}`;
      }
       return this.http.get<Actor[]>(url);
    }

  getSettings(operatorCode: string) {
    const url = this.baseUrl + `/v1/setting/find-by-user`;
    //const url= this.baseUrl + `/v1/setting/${operatorCode}/user-code`;
    //const url= this.baseUrl + `/v1/setting/PROVA/user-code`;
    return this.http.get<any>(url);
  }

  getAslInfo(aslCode?: string) {
    let url = this.baseUrl + `/v1/asl/find-details`;
    if(aslCode) {
      url= url + `?code=${aslCode}`;
    }
    return this.http.get<any[]>(url).pipe(
      map(response => response.length > 0 ? response[0] : null)
    );
  }

    getAllAslInfo(aslCode?: string) {
    let url = this.baseUrl + `/v1/asl/find-details`;
    if(aslCode) {
      url= url + `?code=${aslCode}`;
    }
    return this.http.get<any[]>(url).pipe(
      map(response => response ? response : null)
    );
  }

  getAssociatedSettingsForTransition(transitionUuid: string) {
    const url =
      this.baseUrl + `/v1/setting/${transitionUuid}/associated-settings`;
    //const url= this.baseUrl + `/v1/setting/PROVA/user-code`;
    return this.http.get<any>(url);
  }

  getAllSettings() {
    const url = this.baseUrl + `/v1/setting/find`;
    //const url= this.baseUrl + `/v1/setting/PROVA/user-code`;
    return this.http.get<any>(url);
  }

  getRequest(uuid: string) {
    const url = this.baseUrl + `/v1/transition/${uuid}`;
    return this.http.get<any>(url);
  }

  getForms(uuid: string) {
    const url = this.baseUrl + `/v1/form/${uuid}/transition-forms`;
    return this.http.get<any[]>(url).pipe(
      map(forms => {
        // Sviluppo temporaneo per simulare il versioning dei form
        const grouped: { [code: string]: any[] } = {};
        if(this.authService.isOperatoreSuperUser) {
          forms = forms.filter(form => form.formStatus?.name);
        }
        for (const form of forms) {
          const code = form.formType.code;
          if (!grouped[code]) {
            grouped[code] = [];
          }
          grouped[code].push(form);
        }

        const enrichedForms: any[] = [];
        for (const code of Object.keys(grouped)) {
          const group = grouped[code].sort((a, b) =>
            new Date(b.updateDate ? b.updateDate : b.creationDate).getTime() - new Date(a.updateDate ? a.updateDate : a.creationDate).getTime()
          );

          group.forEach((form, index) => {
            form.replaced = index > 0;
            enrichedForms.push(form);
          });
        }

        return enrichedForms;
      })
    );
  }

  getFormsToAdd(uuid: string) {
    const url = this.baseUrl + `/v1/form/${uuid}/transition-forms-new`;
    return this.http.get<any[]>(url).pipe(
      map(forms => {
        // Sviluppo temporaneo per simulare il versioning dei form
        const grouped: { [code: string]: any[] } = {};
        if(this.authService.isOperatoreSuperUser) {
          forms = forms.filter(form => form.formStatus?.name);
        }
        for (const form of forms) {
          const code = form.formType.code;
          if (!grouped[code]) {
            grouped[code] = [];
          }
          grouped[code].push(form);
        }

        const enrichedForms: any[] = [];
        for (const code of Object.keys(grouped)) {
          const group = grouped[code].sort((a, b) =>
            new Date(b.updateDate ? b.updateDate : b.creationDate).getTime() - new Date(a.updateDate ? a.updateDate : a.creationDate).getTime()
          );

          group.forEach((form, index) => {
            form.replaced = index > 0;
            enrichedForms.push(form);
          });
        }

        return enrichedForms;
      })
    );
  }

  getFormsForSetting(settingCode: string) {
    const url = this.baseUrl + `/v1/form/${settingCode}/find-by-setting`;
    return this.http.get<any[]>(url);
  }

  createTransition(request: any) {
    const url = this.baseUrl + `/v1/transition`;
    return this.http.post<any>(url, request);
  }

  changeStatus(transitionUuid: string, statusCode: string) {
    const url =
      this.baseUrl +
      `/v1/transition/${transitionUuid}/status/${statusCode}/status-change`;
    return this.http.put<any>(url, null);
  }

  changeOwnership(request: any) {
    const url = this.baseUrl + `/v1/ownership/change`;
    return this.http.post<any>(url, request);
  }

  ownershipInfo(uuid: string) {
    const url = this.baseUrl + `/v1/ownership/${uuid}/information`;
    return this.http.get<any>(url);
  }

  completeRequest(request: any, transitionUuid: string) {
    const url = this.baseUrl + `/v1/transition/${transitionUuid}/complete`;
    return this.http.post<any>(url, request);
  }

  deleteRequest(request: any) {
    const url = this.baseUrl + `/v1/transition/delete`;
    return this.http.post<any>(url, request);
  }

  reopenRequest(uuid: string) {
    const url = this.baseUrl + `/v1/transition/${uuid}/reopen`;
    return this.http.put<any>(url, null);
  }

  saveForm(request: any) {
    const url = this.baseUrl + `/v1/transition/save-form`;
    return this.http.post<any>(url, request);
  }

  getStatusStats(
    request: any
  ): Observable<{ total: number; status: string }[]> {
    return this.http.post<{ total: number; status: string }[]>(
      this.baseUrl + `/v1/transition/stats/status`,
      request
    );
  }

  getStatusStatsSuperUser(
    request: any
  ): Observable<{ total: number; status: string }[]> {
    return this.http.post<{ total: number; status: string }[]>(
      this.baseUrl + `/v1/transition/stats/status`,
      request
    );
  }

  getStatusStatsUvm(
    request: any
  ): Observable<{ total: number; status: string }[]> {
    return this.http.post<{ total: number; status: string }[]>(
      this.baseUrl + `/v1/transition/stats/status-uvm`,
      request
    );
  }

  getStatusStatsUvcp(request: any): Observable<{ total: number; status: string }[]> {
    return this.http.post<{ total: number; status: string }[]>(this.baseUrl + `/v1/transition/stats/status-uvcp`, request);
  }

  changeSetting(transitionId: string, subsettingId: string) {
    const request = {
      transitionId,
      subsettingId,
    };
    const url = this.baseUrl + `/v1/transition/change-subsetting`;
    return this.http.put<any>(url, request);
  }

  changeFormStatus(request: any) {
    return this.http.post<any>(
      this.baseUrl + `/v1/transition/change-form-status`,
      request
    );
  }

  getDischargeInfo(transitionUuid: string): Observable<DischargeInfo[]> {
    return this.http.get<DischargeInfo[]>(
      this.baseUrl + `/v1/transition-dates/${transitionUuid}`
    );
  }

  deleteStructureForTransition(transitionUuid: string) {
    return this.http.put<any>(
      this.baseUrl +
        `/v1/transition/${transitionUuid}/facility-remove`,
      null
    );
  }

  print(printCode: string, uuid?: string, compositionId?: string) {
    const url = this.baseUrl + `/v1/report/print?code=${printCode}` + (compositionId ? `&compositionId=${compositionId}` : ``) + (uuid ? `&transitionUuid=${uuid}` : ``);
    return this.http.get(
      url, { responseType: 'blob' }
    );
  }

  deleteDate(id: any) {
    return this.http.delete(this.baseUrl + `/v1/transition-dates/${id}`);
  }

  updateDischargeInfo(data: any) {
  return this.http.put(this.baseUrl + `/v1/transition-dates`, data);
  }

  createDischargeInfo(data: any) {
    return this.http.post(this.baseUrl + `/v1/transition-dates`, data);
  }

  getRequestStatusLabel(value: string): string {
    const found = this.requestStatusOptions.find((o) => o.value === value);
    return found ? found.label : value;
  }
}

